import {
  TrackballControls,
  CameraControls as YomotsuControls,
} from '@react-three/drei'
import CameraControlsImpl from 'camera-controls'
import { PerspectiveCamera, Vector3 } from 'three'
import { useShallow } from 'zustand/react/shallow'
import { useFrame, useThree } from '@react-three/fiber'
import { useCallback, useMemo, RefObject } from 'react'
import { TrackballControls as TrackballControlsImpl } from 'three-stdlib'

import { useCADPageStore, CONTROLS } from '@/state'

interface CameraControlsProps {
  cameraControlsRef: RefObject<CameraControlsImpl | null>
  trackballControlsRef: RefObject<TrackballControlsImpl | null>
}

export const CameraControls = ({
  cameraControlsRef,
  trackballControlsRef,
}: CameraControlsProps) => {
  const camera = useThree((state) => state.camera)
  const targetVector = useMemo(() => new Vector3(), [])
  const target = useCADPageStore(useShallow((state) => state.target))
  const setState = useCADPageStore(useShallow((state) => state.setState))
  const controlsId = useCADPageStore(useShallow((state) => state.controlsId))

  useFrame((_, delta) => {
    cameraControlsRef.current?.update(delta)
  })

  const updateCameraState = useCallback(() => {
    if (camera instanceof PerspectiveCamera) {
      const cameraState = {
        cameraAspect: camera.aspect,
        cameraPosition: camera.position.toArray(),
        cameraFov: camera.fov,
        cameraZoom: camera.zoom,
        cameraQuaternion: camera.quaternion.toArray(),
        cameraUp: camera.up.toArray(),
      }

      if (controlsId === CONTROLS.TRACKBALL_CONTROLS) {
        setState({
          target: trackballControlsRef.current?.target.toArray(),
          ...cameraState,
        })
      } else if (controlsId === CONTROLS.CAMERA_CONTROLS) {
        setState({
          target: cameraControlsRef.current?.getTarget(targetVector).toArray(),
          ...cameraState,
        })
      }
    }
  }, [
    camera,
    setState,
    targetVector,
    cameraControlsRef,
    trackballControlsRef,
    controlsId,
  ])

  return (
    <>
      {controlsId === CONTROLS.CAMERA_CONTROLS && (
        <YomotsuControls
          makeDefault={false}
          ref={cameraControlsRef}
          camera={camera}
          enabled={false}
          onChange={updateCameraState}
          minPolarAngle={0}
          maxPolarAngle={Math.PI}
          minDistance={0.0000001}
          maxDistance={100}
          dampingFactor={0}
          dollySpeed={2.5}
          truckSpeed={2.5}
        />
      )}
      <TrackballControls
        makeDefault
        enabled={controlsId === CONTROLS.TRACKBALL_CONTROLS}
        ref={trackballControlsRef}
        camera={camera}
        target={[target[0], target[1], target[2]]}
        onChange={updateCameraState}
        zoomSpeed={2.5}
        dynamicDampingFactor={0}
        staticMoving={true}
        rotateSpeed={2.5}
      />
    </>
  )
}
